import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ActivityIndicator,
  ScrollView,
  Dimensions,
  Platform,
  useWindowDimensions,
} from 'react-native';
import { LineChart } from 'react-native-chart-kit';
import { Line, Circle } from 'react-native-svg';
import { COLORS } from '../constants/theme';
import {
  findClosestPriceAtOrBefore,
  findNearestIndexByTime,
  getTimeMs,
  interpolateTimeAlongSampledPoints,
  mapTimeToSampledChartX,
  UnderlyingHoverMeta,
} from '../utils/performanceChartHover';

// Helper function to get English month abbreviations
const getEnglishMonthShort = (date: Date): string => {
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return months[date.getMonth()];
};

// Helper function to format date in English
const formatDateEnglish = (date: Date): string => {
  const day = date.getDate().toString().padStart(2, '0');
  const month = getEnglishMonthShort(date);
  const year = date.getFullYear();
  return `${day} ${month} ${year}`;
};

/**
 * Product codes (warrants) that use premium % as chart base instead of 100%.
 * For these types, the issue price corresponds to the premium; the chart should
 * show the warrant in percentage terms with the premium as reference.
 */
const PREMIUM_PRODUCT_CODES = ['2100', '2110', '2200'];
const PRODUCT_LINE_COLOR = '#E53935';
const UNDERLYING_COLORS = ['#00C853', '#1976D2', '#00ACC1'];

/**
 * Returns the base percentage for normalizing the product series in the chart.
 * For premium product types (2100, 2110, 2200), uses PROPremium (premiumPct) when
 * available; otherwise falls back to 100 (standard behavior).
 */
function getProductBasePercentage(product: any): number {
  const code = product?.productCode != null ? String(product.productCode) : '';
  if (!PREMIUM_PRODUCT_CODES.includes(code)) return 100;
  const premiumPct = product?.premiumPct;
  if (premiumPct == null || typeof premiumPct !== 'number' || premiumPct <= 0) return 100;
  return premiumPct;
}

/** Must match react-native-chart-kit LineChart defaults (style.paddingTop / paddingRight). */
const LINE_CHART_PADDING_TOP = 16;
const LINE_CHART_PADDING_RIGHT = 64;

/** Y coordinate for a chart value — mirrors LineChart calcHeight + renderLine (75% vertical area). */
function calcLineChartKitY(
  value: number,
  datas: readonly number[],
  height: number,
  paddingTop: number = LINE_CHART_PADDING_TOP,
): number {
  if (datas.length === 0) {
    return paddingTop;
  }
  const min = Math.min(...datas);
  const max = Math.max(...datas);
  const scaler = max - min || 1;
  const calcHeight = height * ((value - min) / scaler);
  return ((height - calcHeight) / 4) * 3 + paddingTop;
}

interface TooltipData {
  x: number;
  y: number;
  index: number;
  date: string;
  values: Array<{ name: string; value: number; color: string; price?: number }>;
}

interface ProductPerformanceChartProps {
  activeTab: 'Week' | 'Month' | 'Year' | 'YTD' | 'All';
  onTabChange: (tab: 'Week' | 'Month' | 'Year' | 'YTD' | 'All') => void;
  priceHistory: Array<{ date: string; price: number }>;
  fullPriceHistory?: Array<{ date: string; price: number }>; // Full history for consistent base price
  underlyingsHistory: Array<any>;
  fullUnderlyingsHistory?: Array<any>; // Full history for consistent base price
  visibleUnderlyings: boolean[];
  loadingHistory: boolean;
  product: any;
  calculatePerformance: () => string;
  isDesktopCombined?: boolean;
  selectedUnderlyingIndex?: number;
  onSelectUnderlyingIndex?: (index: number) => void;

  isFocused?: boolean;
  onShowAllUnderlyings?: () => void;
  /** Si false, no se muestran underlyings ni su leyenda en el chart (por config del tipo de producto) */
  showUnderlyingsInChart?: boolean;
  /** Si false, no se muestra el precio en el tooltip (solo porcentaje) */
  showTooltipPrice?: boolean;
}

export const ProductPerformanceChart: React.FC<ProductPerformanceChartProps> = ({
  activeTab,
  onTabChange,
  priceHistory,
  fullPriceHistory,
  underlyingsHistory,
  fullUnderlyingsHistory,
  visibleUnderlyings,
  loadingHistory,
  product,
  calculatePerformance,
  isDesktopCombined = false,
  selectedUnderlyingIndex,
  isFocused,
  onSelectUnderlyingIndex,
  onShowAllUnderlyings,
  showUnderlyingsInChart = true,
  showTooltipPrice = true,
}) => {
  const [tooltip, setTooltip] = useState<TooltipData | null>(null);
  const chartContainerRef = useRef<View>(null);
  const [containerWidth, setContainerWidth] = useState<number>(0);
  const [chartContainerWidth, setChartContainerWidth] = useState<number>(0);
  const [actualSVGWidth, setActualSVGWidth] = useState<number>(0);

  useEffect(() => {
    setTooltip(null);
  }, [activeTab]);
  
  // Use useWindowDimensions for responsive width that updates on resize
  const { width: windowWidth } = useWindowDimensions();
  
  // Calculate effective chart width - use container width if available, otherwise window width
  const effectiveWidth = containerWidth > 0 ? containerWidth : windowWidth;

  // DEFINITIVE SOLUTION: Inject CSS and force SVG width using !important (web only)
  useEffect(() => {
    if (Platform.OS === 'web') {
      // Use chartContainerWidth if available, otherwise containerWidth, otherwise effectiveWidth
      const availableWidth = chartContainerWidth > 0 ? chartContainerWidth : (containerWidth > 0 ? containerWidth : effectiveWidth);
      const maxWidth = Math.max(300, availableWidth);
      
      // Inject global CSS style to force all SVGs to respect max width
      const styleId = 'force-svg-width-style';
      let styleElement = document.getElementById(styleId) as HTMLStyleElement;
      
      if (!styleElement) {
        styleElement = document.createElement('style');
        styleElement.id = styleId;
        document.head.appendChild(styleElement);
      }
      
      // Remove global CSS that affects all SVGs - only modify SVGs within our container
      // This prevents breaking scroll and other page elements
      if (styleElement) {
        styleElement.textContent = ''; // Clear any previous styles
      }
      
      const forceSVGToFit = () => {
        try {
          // Only target SVGs within our chart container to avoid affecting other page elements
          if (chartContainerRef.current) {
            try {
              // @ts-ignore - Access native node for web
              const containerNode = chartContainerRef.current._nativeNode || chartContainerRef.current;
              
              if (containerNode) {
                // Only target SVGs within our container
                const containerSvgs = containerNode.querySelectorAll('svg');
                
                containerSvgs.forEach((svg: any) => {
                  const currentWidth = parseInt(svg.getAttribute('width') || '0');
                  
                  // Only adjust if SVG width doesn't match container width (especially for Week)
                  if (currentWidth > 0 && currentWidth < maxWidth * 0.9) {
                    // For Week with few points, ensure SVG uses full width
                    svg.style.setProperty('max-width', '100%', 'important');
                    svg.style.setProperty('width', '100%', 'important');
                  } else if (currentWidth > maxWidth * 1.1) {
                    // Limit if too large (only if significantly larger)
                    svg.style.setProperty('max-width', '100%', 'important');
                    svg.style.setProperty('width', '100%', 'important');
                  }
                });
              }
            } catch (e) {
              // Ignore if we can't access native node
            }
          }
        } catch (error) {
          console.error('Error forcing SVG to fit:', error);
        }
      };
      
      // Force fit multiple times
      forceSVGToFit();
      const timeouts = [50, 100, 200, 400, 800, 1500, 3000].map(delay => 
        setTimeout(forceSVGToFit, delay)
      );
      
      return () => {
        timeouts.forEach(clearTimeout);
      };
    }
  }, [priceHistory, activeTab, effectiveWidth, containerWidth, chartContainerWidth]);

  // Effect to detect actual SVG width when chart renders
  useEffect(() => {
    if (Platform.OS === 'web' && chartContainerRef.current) {
      const detectSVGWidth = () => {
        try {
          // @ts-ignore - Access native node for web
          const containerNode = chartContainerRef.current._nativeNode || chartContainerRef.current;
          if (containerNode) {
            const svgElement = containerNode.querySelector('svg');
            if (svgElement) {
              const svgWidth = parseFloat(svgElement.getAttribute('width') || '0');
              if (svgWidth > 0 && Math.abs(svgWidth - actualSVGWidth) > 1) {
                setActualSVGWidth(svgWidth);
              }
            }
          }
        } catch (e) {
          // Ignore errors
        }
      };
      
      // Try to detect SVG width multiple times after render
      const timeouts = [100, 200, 400, 800, 1500].map(delay => 
        setTimeout(detectSVGWidth, delay)
      );
      
      return () => {
        timeouts.forEach(clearTimeout);
      };
    }
  }, [priceHistory, activeTab, actualSVGWidth]);

  // Calculate chart dimensions outside renderChart so they're available for tooltip positioning
  // These will be updated inside renderChart based on actual available width
  const viewportWidth = effectiveWidth;
  const maxViewportWidth = Math.min(viewportWidth, 1920);
  
  // Initial calculation - will be refined in renderChart based on chartContainerWidth
  let chartWidth: number;
  if (activeTab === 'All') {
    const absoluteMaxWidth = 1300;
    chartWidth = Math.min(maxViewportWidth - 15, absoluteMaxWidth);
  } else {
    // Use chartContainerWidth if available, otherwise fallback
    const availableWidth = chartContainerWidth > 0 ? chartContainerWidth : (containerWidth > 0 ? containerWidth : effectiveWidth);
    chartWidth = Math.max(300, availableWidth);
  }
  const actualChartWidth = chartWidth;
  const chartHeight = Platform.OS === 'web' ? 320 : 220;

  const renderChart = () => {
    console.log(`📊 ProductPerformanceChart renderChart - activeTab: ${activeTab}, priceHistory length: ${priceHistory?.length || 0}, loadingHistory: ${loadingHistory}`);
    
    if (loadingHistory) {
      return (
        <View style={styles.chartLoadingContainer}>
          <ActivityIndicator size="small" color={COLORS.primary} />
        </View>
      );
    }

    if (!priceHistory || priceHistory.length === 0) {
      console.log(`📊 ProductPerformanceChart - No data available for ${activeTab}. priceHistory:`, priceHistory);
      return (
        <View style={styles.chartLoadingContainer}>
          <Text style={styles.noDataText}>No data available</Text>
        </View>
      );
    }

    // Draw every trading day in the selected period. Hover already uses the
    // full series; sampling the polyline to ~35 points made the line miss the
    // dots (chart-kit still honors the width prop, so the old cap is not needed).
    const fullPeriodHistory = [...priceHistory].sort((a, b) => {
      const dateA = new Date(a.date).getTime();
      const dateB = new Date(b.date).getTime();
      return dateA - dateB;
    });
    const sortedPriceHistory = fullPeriodHistory;

    // Prepare datasets for the chart
    const datasets: any[] = [];
    const dataLength = sortedPriceHistory.length;

    // Add product dataset (normalized to percentage)
    // Use first price from FULL history as base to ensure consistency across all periods
    let productBasePrice = 100;
    if (fullPriceHistory && fullPriceHistory.length > 0) {
      // Sort full history to get the earliest price
      const sortedFullHistory = [...fullPriceHistory].sort((a, b) => {
        const dateA = new Date(a.date).getTime();
        const dateB = new Date(b.date).getTime();
        return dateA - dateB;
      });
      productBasePrice = sortedFullHistory[0]?.price || 100;
    } else if (sortedPriceHistory.length > 0) {
      // Fallback to filtered history if full history not available
      productBasePrice = sortedPriceHistory[0]?.price || 100;
    }

    // For premium product types (2100, 2110, 2200), use premiumPct as base so the chart shows premium % instead of 100%
    const basePercentage = getProductBasePercentage(product);
    const isPremiumProduct = PREMIUM_PRODUCT_CODES.includes(String(product?.productCode ?? ''));

    if (sortedPriceHistory.length > 0) {

      const normalizedData = sortedPriceHistory.map((item) => {
        const normalized = (item.price / productBasePrice) * basePercentage;
        return isNaN(normalized) || !isFinite(normalized) ? basePercentage : normalized;
      });

      datasets.push({
        data: normalizedData,
        color: () => PRODUCT_LINE_COLOR,
        strokeWidth: 2,
      });
    }

    // For premium products: warrant range for scaling underlyings; underlyings in price with right axis
    let warrantDataMin = 0;
    let warrantDataMax = basePercentage;
    if (isPremiumProduct && datasets[0]?.data?.length) {
      warrantDataMin = Math.min(...datasets[0].data);
      warrantDataMax = Math.max(...datasets[0].data);
    }
    let underlyingPriceRange: { min: number; max: number } | null = null;
    const underlyingHoverMetas: UnderlyingHoverMeta[] = [];

    // Add underlyings datasets
    // Premium (2100, 2110, 2200): underlyings in price, scaled to warrant range; right axis = price
    // Others: initialFixingLevel as base, 100% at inception
    underlyingsHistory.forEach((underlying, index) => {
      const isVisible = visibleUnderlyings[index] !== false;

      if (
        isVisible &&
        showUnderlyingsInChart &&
        underlying.history &&
        underlying.history.length > 0
      ) {
        const underlyingData: number[] = [];
        const rawPrices: number[] = [];
        let hoverBasePrice: number | null = null;
        let hoverPriceMin: number | null = null;
        let hoverPriceMax: number | null = null;
        
        // Sort underlying history by date
        const sortedUnderlyingHistory = [...underlying.history].sort((a: any, b: any) => {
          const dateA = new Date(a.date).getTime();
          const dateB = new Date(b.date).getTime();
          return dateA - dateB;
        });

        if (isPremiumProduct) {
          const warrantRange = warrantDataMax - warrantDataMin || 1;
          let priceMin = Infinity;
          let priceMax = -Infinity;
          sortedPriceHistory.forEach((productDataPoint) => {
            const productDateStr = new Date(productDataPoint.date).toDateString();
            const exactMatch = sortedUnderlyingHistory.find((up: any) =>
              new Date(up.date).toDateString() === productDateStr
            );
            let price: number | null = exactMatch?.price > 0 ? exactMatch.price : null;
            if (price == null) {
              const productDate = new Date(productDataPoint.date).getTime();
              let closest: any = null;
              let closestDiff = Infinity;
              for (const up of sortedUnderlyingHistory) {
                const ud = new Date(up.date).getTime();
                if (ud <= productDate && productDate - ud < closestDiff) {
                  closestDiff = productDate - ud;
                  closest = up;
                }
              }
              if (closest?.price > 0) price = closest.price;
            }
            if (price != null && price > 0) {
              if (price < priceMin) priceMin = price;
              if (price > priceMax) priceMax = price;
            }
            rawPrices.push(price ?? 0);
          });
          const span = priceMax - priceMin || 1;
          sortedPriceHistory.forEach((_, pi) => {
            const raw = rawPrices[pi];
            if (raw > 0) {
              const scaled = ((raw - priceMin) / span) * warrantRange + warrantDataMin;
              underlyingData.push(isNaN(scaled) || !isFinite(scaled) ? warrantDataMin : scaled);
            } else if (underlyingData.length > 0) {
              underlyingData.push(underlyingData[underlyingData.length - 1]);
            } else {
              underlyingData.push(warrantDataMin);
            }
          });
          hoverPriceMin = priceMin === Infinity ? null : priceMin;
          hoverPriceMax = priceMax === -Infinity ? null : priceMax;
          if (priceMin !== Infinity && priceMax !== -Infinity) {
            if (underlyingPriceRange == null) underlyingPriceRange = { min: priceMin, max: priceMax };
            else {
              underlyingPriceRange.min = Math.min(underlyingPriceRange.min, priceMin);
              underlyingPriceRange.max = Math.max(underlyingPriceRange.max, priceMax);
            }
          }
        } else {
        // CRITICAL: Always use initialFixingLevel from product.underlyings as base price
        // This ensures all stocks start at 100% at inception, regardless of filtered history
        // Match by name or ISIN to ensure we get the correct underlying
        let underlyingBasePrice: number | null = null;
        
        // First try to get from product.underlyings by matching name or ISIN
        if (product?.underlyings && product.underlyings.length > 0) {
          const matchingProductUnderlying = product.underlyings.find(
            (u: any) => 
              (u.name && underlying.name && u.name === underlying.name) ||
              (u.isin && underlying.isin && u.isin === underlying.isin)
          );
          
          if (matchingProductUnderlying?.initialFixingLevel > 0) {
            underlyingBasePrice = matchingProductUnderlying.initialFixingLevel;
          }
        }
        
        // Fallback to fullUnderlyingsHistory if product.underlyings not available
        if ((!underlyingBasePrice || underlyingBasePrice <= 0) && fullUnderlyingsHistory?.[index]?.initialFixingLevel > 0) {
          underlyingBasePrice = fullUnderlyingsHistory![index].initialFixingLevel;
        }
        
        // Fallback to underlying.initialFixingLevel from history data
        if ((!underlyingBasePrice || underlyingBasePrice <= 0) && underlying.initialFixingLevel > 0) {
          underlyingBasePrice = underlying.initialFixingLevel;
        }
        
        // Last resort: use first price from full history (sorted by date)
        if (!underlyingBasePrice || underlyingBasePrice <= 0) {
          if (fullUnderlyingsHistory && fullUnderlyingsHistory[index]?.history) {
            const sortedFullUnderlyingHistory = [...fullUnderlyingsHistory[index].history].sort((a: any, b: any) => {
              const dateA = new Date(a.date).getTime();
              const dateB = new Date(b.date).getTime();
              return dateA - dateB;
            });
            underlyingBasePrice = sortedFullUnderlyingHistory[0]?.price;
          }
        }
        
        // Final fallback: use first price from filtered history
        if (!underlyingBasePrice || underlyingBasePrice <= 0) {
          underlyingBasePrice = sortedUnderlyingHistory[0]?.price;
        }
        
        // Skip if no valid base price
        if (!underlyingBasePrice || underlyingBasePrice <= 0) {
          console.warn(`⚠️ No valid base price found for underlying ${underlying.name || index}`);
          return;
        }
        hoverBasePrice = underlyingBasePrice;

        // Get initialFixing date from product to identify inception date
        const initialFixingDate = product?.initialFixing ? new Date(product.initialFixing).toDateString() : null;
        
        // Map underlying history to match product history by date
        // Use exact date match only - this ensures consistency across all periods
        sortedPriceHistory.forEach((productDataPoint, productIndex) => {
          const productDateStr = new Date(productDataPoint.date).toDateString();
          const isInceptionDate = initialFixingDate && productDateStr === initialFixingDate;
          
          // Find exact date match only - no interpolation
          // This ensures the same price is used regardless of period
          const exactMatch = sortedUnderlyingHistory.find((underlyingPoint: any) => {
            const underlyingDateStr = new Date(underlyingPoint.date).toDateString();
            return underlyingDateStr === productDateStr;
          });
          
          let matchingPrice: number | null = null;
          if (exactMatch && exactMatch.price > 0) {
            matchingPrice = exactMatch.price;
          }
          
          // Normalize and add to data
          if (matchingPrice !== null && matchingPrice > 0) {
            const normalized = (matchingPrice / underlyingBasePrice) * 100;
            // CRITICAL: At inception date, the price should be exactly 100%
            // This ensures all stocks start at 100% at inception
            // Also check if the normalized value is very close to 100% (within 0.5%) and force it to 100%
            let finalValue = isNaN(normalized) || !isFinite(normalized) ? 100 : normalized;
            
            // If this is the inception date OR the first point, it should be exactly 100%
            if (isInceptionDate || productIndex === 0) {
              finalValue = 100;
            } else if (Math.abs(finalValue - 100) < 0.5) {
              // If normalized value is very close to 100%, force it to exactly 100%
              // This handles cases where the price at inception is slightly different from initialFixingLevel
              finalValue = 100;
            }
            
            underlyingData.push(finalValue);
          } else {
            // If no exact match found, try to find the closest previous date
            // This handles gaps in data (weekends, holidays)
            const productDate = new Date(productDataPoint.date).getTime();
            let closestMatch: any = null;
            let closestDiff = Infinity;
            
            for (const underlyingPoint of sortedUnderlyingHistory) {
              const underlyingDate = new Date(underlyingPoint.date).getTime();
              // Only consider dates before or equal to the product date
              if (underlyingDate <= productDate) {
                const diff = productDate - underlyingDate;
                if (diff < closestDiff) {
                  closestDiff = diff;
                  closestMatch = underlyingPoint;
                }
              }
            }
            
            if (closestMatch && closestMatch.price > 0) {
              const normalized = (closestMatch.price / underlyingBasePrice) * 100;
              const finalValue = isNaN(normalized) || !isFinite(normalized) ? 100 : normalized;
              underlyingData.push(finalValue);
            } else if (underlyingData.length > 0) {
              // Use the last known value to continue the line
              underlyingData.push(underlyingData[underlyingData.length - 1]);
            } else {
              // No data available, use 100 as base
              underlyingData.push(100);
            }
          }
        });
        } // end non-premium branch

        // Only add if we have valid data
        if (underlyingData.length > 0 && underlyingData.every(v => !isNaN(v) && isFinite(v))) {
          datasets.push({
            data: underlyingData,
            color: () => UNDERLYING_COLORS[index % UNDERLYING_COLORS.length],
            strokeWidth: 1.5,
          });
          underlyingHoverMetas.push({
            sourceIndex: index,
            name: underlying.name || `Underlying ${index + 1}`,
            history: sortedUnderlyingHistory.map((point: { date: string; price: number }) => ({
              date: point.date,
              price: Number(point.price),
            })),
            basePrice: hoverBasePrice,
            priceMin: hoverPriceMin,
            priceMax: hoverPriceMax,
          });
        }
      }
    });

    // Verify all datasets have the same length
    const allSameLength = datasets.every((d) => d.data.length === dataLength);
    if (!allSameLength) {
      datasets.forEach((dataset, datasetIndex) => {
        const paddingFallback = datasetIndex === 0 ? basePercentage : (isPremiumProduct ? warrantDataMin : 100);
        while (dataset.data.length < dataLength) {
          dataset.data.push(dataset.data[dataset.data.length - 1] || paddingFallback);
        }
        dataset.data = dataset.data.slice(0, dataLength);
      });
    }

    // PROStrike / PROBarrierLevel are % of par (same as At a Glance) — map to chart scale, not first historical price
    const rawStrikeLevel = product?.strikeLevel ?? null;
    const rawBarrierLevel = product?.barrierLevel ?? null;
    let normalizedStrikeLevel: number | null = null;
    let normalizedBarrierLevel: number | null = null;
    if (rawStrikeLevel != null && Number.isFinite(rawStrikeLevel)) {
      normalizedStrikeLevel = (rawStrikeLevel / 100) * basePercentage;
    }
    if (rawBarrierLevel != null && Number.isFinite(rawBarrierLevel)) {
      normalizedBarrierLevel = (rawBarrierLevel / 100) * basePercentage;
    }
    
    // Add invisible datasets for strike and barrier to force Y-axis range to include them
    // These datasets are completely transparent (opacity 0) so they don't show as lines
    if (normalizedStrikeLevel !== null && normalizedStrikeLevel > 0) {
      datasets.push({
        data: new Array(dataLength).fill(normalizedStrikeLevel),
        color: () => 'rgba(0,0,0,0)', // Completely transparent
        strokeWidth: 0,
        withDots: false,
      });
    }
    
    if (normalizedBarrierLevel !== null && normalizedBarrierLevel > 0) {
      datasets.push({
        data: new Array(dataLength).fill(normalizedBarrierLevel),
        color: () => 'rgba(0,0,0,0)', // Completely transparent
        strokeWidth: 0,
        withDots: false,
      });
    }

    if (datasets.length === 0 || datasets[0].data.length === 0) {
      return (
        <View style={styles.chartLoadingContainer}>
          <Text style={styles.noDataText}>No data available for the selected period</Text>
        </View>
      );
    }

    // Validate all data points are valid numbers
    const hasInvalidData = datasets.some((dataset) =>
      dataset.data.some((val: number) => isNaN(val) || !isFinite(val)),
    );

    if (hasInvalidData) {
      return (
        <View style={styles.chartLoadingContainer}>
          <Text style={styles.noDataText}>Error in chart data</Text>
        </View>
      );
    }

    // Format labels based on active tab (using already sorted data)
    const labels = sortedPriceHistory.map((item, index) => {
      const date = new Date(item.date);
      
      // For Week, ALWAYS show all labels - no filtering
      if (activeTab === 'Week') {
        // Use shorter format for Week to ensure all labels fit
        const day = date.getDate();
        const monthShort = getEnglishMonthShort(date);
        return `${day} ${monthShort}`;
      }
      
      // For other periods, calculate how many labels to show
      let labelsToShow = 4; // Default
      if (activeTab === 'Month') {
        labelsToShow = Math.min(6, sortedPriceHistory.length);
      } else if (activeTab === 'Year' || activeTab === 'YTD') {
        labelsToShow = Math.min(8, Math.max(6, sortedPriceHistory.length));
      } else {
        // All: show more labels for better readability
        labelsToShow = Math.min(12, Math.max(8, Math.floor(sortedPriceHistory.length / 10)));
      }
      
      // Calculate interval to distribute labels evenly for other periods
      const interval = sortedPriceHistory.length > 1 ? (sortedPriceHistory.length - 1) / (labelsToShow - 1) : 1;
      
      // Check if this index should show a label
      const shouldShowLabel = 
        index === 0 || 
        index === sortedPriceHistory.length - 1 || 
        Math.abs(index - Math.round(index / interval) * interval) < 0.5;

      if (!shouldShowLabel) return '';

      if (activeTab === 'Month') {
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        return `${day}/${month}`;
      } else if (activeTab === 'Year' || activeTab === 'YTD') {
        const monthShort = getEnglishMonthShort(date);
        const yearShort = date.getFullYear().toString().slice(-2);
        return `${monthShort} ${yearShort}`;
      } else {
        // All period: show month and year
        const monthShort = getEnglishMonthShort(date);
        const year = date.getFullYear();
        return `${monthShort} ${year}`;
      }
    });

    // Use the already normalized strike and barrier levels from above
    const strikeLevel = normalizedStrikeLevel;
    const barrierLevel = normalizedBarrierLevel;

    // Same flat data array react-native-chart-kit uses for min/max and Y scaling
    const chartKitDatas = datasets.flatMap(d => d.data);
    const chartYMin = chartKitDatas.length > 0 ? Math.min(...chartKitDatas) : 0;
    const chartYMax = chartKitDatas.length > 0 ? Math.max(...chartKitDatas) : 100;
    const chartYRange = chartYMax - chartYMin || 1;

    // Use chart dimensions from outer scope (calculated above)
    // Update actualChartWidth based on available width
    const availableWidth = chartContainerWidth > 0 ? chartContainerWidth : (containerWidth > 0 ? containerWidth : effectiveWidth);
    const finalChartWidth = activeTab === 'All' 
      ? Math.min(Math.min(viewportWidth, 1920) - 15, 1300)
      : Math.max(300, availableWidth);
    
    console.log(`📊 Chart dimensions - viewportWidth: ${viewportWidth}, containerWidth: ${containerWidth}, chartContainerWidth: ${chartContainerWidth}, finalChartWidth: ${finalChartWidth}, actualChartWidth: ${actualChartWidth}, dataPoints: ${sortedPriceHistory.length}, activeTab: ${activeTab}`);

    if (!datasets || datasets.length === 0 || !labels || labels.length === 0) {
      return (
        <View style={styles.chartLoadingContainer}>
          <Text style={styles.noDataText}>Chart data not available</Text>
        </View>
      );
    }

    const fullPeriodTimes = fullPeriodHistory.map((point) => getTimeMs(point.date));
    const sampledTimes = sortedPriceHistory.map((point) => getTimeMs(point.date));
    const getChartGraphWidth = (): number => finalChartWidth - LINE_CHART_PADDING_RIGHT;

    const resolveUnderlyingChartValue = (
      meta: UnderlyingHoverMeta,
      rawPrice: number,
      isInception: boolean,
    ): number => {
      if (isPremiumProduct && meta.priceMin != null && meta.priceMax != null) {
        const warrantRange = warrantDataMax - warrantDataMin || 1;
        const span = meta.priceMax - meta.priceMin || 1;
        const scaled = ((rawPrice - meta.priceMin) / span) * warrantRange + warrantDataMin;
        return isNaN(scaled) || !isFinite(scaled) ? warrantDataMin : scaled;
      }
      if (meta.basePrice && meta.basePrice > 0) {
        if (isInception) {
          return 100;
        }
        const normalized = (rawPrice / meta.basePrice) * 100;
        if (isNaN(normalized) || !isFinite(normalized)) {
          return 100;
        }
        if (Math.abs(normalized - 100) < 0.5) {
          return 100;
        }
        return normalized;
      }
      return 100;
    };

    const buildTooltipFromFullIndex = (fullIndex: number, overrideX?: number): TooltipData | null => {
      const priceData = fullPeriodHistory[fullIndex];
      if (!priceData) {
        return null;
      }

      const snappedX = overrideX ?? mapTimeToSampledChartX(
        sampledTimes,
        fullPeriodTimes[fullIndex],
        LINE_CHART_PADDING_RIGHT,
        getChartGraphWidth(),
      );

      let formattedDate = priceData.date;
      try {
        formattedDate = formatDateEnglish(new Date(priceData.date));
      } catch {
        formattedDate = priceData.date;
      }

      const productNormalized = (priceData.price / productBasePrice) * basePercentage;
      const productValue = isNaN(productNormalized) || !isFinite(productNormalized)
        ? basePercentage
        : productNormalized;

      const values: TooltipData['values'] = [{
        name: 'Product',
        value: productValue,
        color: PRODUCT_LINE_COLOR,
        price: showTooltipPrice ? priceData.price : undefined,
      }];

      const initialFixingDate = product?.initialFixing
        ? new Date(product.initialFixing).toDateString()
        : null;
      const isInception = Boolean(
        initialFixingDate && new Date(priceData.date).toDateString() === initialFixingDate
      );

      underlyingHoverMetas.forEach((meta) => {
        const rawPoint = findClosestPriceAtOrBefore(meta.history, fullPeriodTimes[fullIndex]);
        if (!rawPoint || rawPoint.price <= 0) {
          return;
        }
        values.push({
          name: meta.name,
          value: resolveUnderlyingChartValue(meta, rawPoint.price, isInception),
          color: UNDERLYING_COLORS[meta.sourceIndex % UNDERLYING_COLORS.length],
          price: showTooltipPrice ? rawPoint.price : undefined,
        });
      });

      return {
        x: snappedX,
        y: calcLineChartKitY(productValue, chartKitDatas, chartHeight),
        index: fullIndex,
        date: formattedDate,
        values,
      };
    };

    // Hover snaps to the nearest trading day in the full period, not the sampled vertices.
    const updateTooltipFromMousePosition = (mouseX: number, mouseY: number, containerElement: any) => {
      if (!containerElement || !fullPeriodHistory.length) return;
      
      const rect = containerElement.getBoundingClientRect();
      const scrollLeft = containerElement.scrollLeft || 0;
      const relativeX = mouseX - rect.left + scrollLeft;
      
      // Get the actual SVG width - react-native-chart-kit renders an SVG, we need its actual width
      // Try to find the SVG element within the container
      let currentSVGWidth = actualChartWidth;
      try {
        const svgElement = containerElement.querySelector('svg');
        if (svgElement) {
          const svgWidth = parseFloat(svgElement.getAttribute('width') || '0');
          if (svgWidth > 0) {
            currentSVGWidth = svgWidth;
            // Update state if different
            if (Math.abs(currentSVGWidth - actualSVGWidth) > 1) {
              setActualSVGWidth(currentSVGWidth);
            }
          }
        }
      } catch (e) {
        // Fallback to calculated width if SVG not found
        currentSVGWidth = chartContainerWidth > 0 ? chartContainerWidth : (rect.width || actualChartWidth);
      }
      
      const graphWidth = getChartGraphWidth();
      const relativeGraphX = relativeX - LINE_CHART_PADDING_RIGHT;
      
      if (relativeGraphX < 0 || relativeGraphX > graphWidth) {
        setTooltip(null);
        return;
      }

      const relativeT = graphWidth > 0 ? relativeGraphX / graphWidth : 0;
      const targetTime = interpolateTimeAlongSampledPoints(sampledTimes, relativeT);
      const fullIndex = findNearestIndexByTime(fullPeriodTimes, targetTime);
      const nextTooltip = buildTooltipFromFullIndex(fullIndex);
      if (nextTooltip) {
        setTooltip(nextTooltip);
      }
    };

    return (
      <View 
        ref={chartContainerRef}
        style={{ 
          width: '100%',
          maxWidth: '100%',
          overflow: 'hidden',
          position: 'relative',
        }}
        onLayout={(event) => {
          const { width } = event.nativeEvent.layout;
          if (width > 0 && width !== chartContainerWidth) {
            setChartContainerWidth(width);
          }
        }}
      >
        <View
          style={{
            width: '100%',
            maxWidth: '100%',
            overflow: 'hidden',
            position: 'relative',
          }}
        >
          <ScrollView
            horizontal={false}
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={styles.chartScrollContent}
            style={[
              styles.chartContainer, 
              { width: '100%', maxWidth: '100%' }
            ]}
          {...(Platform.OS === 'web' && {
            // @ts-ignore - React Native Web supports onMouseMove
            onMouseMove: (e: any) => {
              updateTooltipFromMousePosition(e.clientX, e.clientY, e.currentTarget);
            },
            // @ts-ignore
            onMouseLeave: () => {
              setTooltip(null);
            },
          })}
        >
          <View 
            style={{ 
              flexDirection: isPremiumProduct && underlyingPriceRange ? 'row' : 'column',
              width: '100%',
              maxWidth: '100%',
              overflow: 'hidden',
              ...(Platform.OS === 'web' && { position: 'relative' as const }),
            }}
          >
            <View style={{ overflow: 'hidden', width: isPremiumProduct && underlyingPriceRange ? finalChartWidth : '100%' }}>
            <LineChart
            data={{
              labels,
              datasets,
            }}
            width={finalChartWidth}
            height={chartHeight}
          withInnerLines={true}
          withOuterLines={true}
          withVerticalLines={false}
          withHorizontalLines={true}
          withDots={false}
          withShadow={false}
          withVerticalLabels={true}
          withHorizontalLabels={true}
          chartConfig={{
            backgroundColor: COLORS.white,
            backgroundGradientFrom: COLORS.white,
            backgroundGradientTo: COLORS.white,
            decimalPlaces: 0,
            color: (opacity = 1) => `rgba(0, 0, 0, ${opacity * 0.3})`,
            labelColor: (opacity = 1) => `rgba(0, 0, 0, ${opacity * 0.5})`,
            style: {
              borderRadius: 0,
            },
            propsForBackgroundLines: {
              strokeDasharray: '',
              stroke: '#E0E0E0',
              strokeWidth: 1,
            },
            propsForLabels: {
              fontSize: activeTab === 'Week' ? 9 : 10,
              // For Week, ensure all labels are visible by reducing rotation and adjusting spacing
              ...(activeTab === 'Week' && {
                rotation: 0,
                dx: 0,
                dy: 5,
                textAnchor: 'middle',
              }),
            },
          }}
          style={{
            marginVertical: 0,
            borderRadius: 0,
          }}
          onDataPointClick={(data) => {
            const priceData = sortedPriceHistory[data.index];
            if (!priceData) {
              return;
            }
            const fullIndex = findNearestIndexByTime(fullPeriodTimes, getTimeMs(priceData.date));
            const nextTooltip = buildTooltipFromFullIndex(fullIndex, data.x);
            if (nextTooltip) {
              setTooltip(nextTooltip);
            }
          }}
          decorator={() => {
            const decoratorChartWidth = finalChartWidth;
            const lineStartX = LINE_CHART_PADDING_RIGHT;
            const lineEndX = decoratorChartWidth;

            const calcY = (value: number) =>
              calcLineChartKitY(value, chartKitDatas, chartHeight);

            return (
              <>
                {/* Strike Level - Black dotted line (no text label) - constrained to chart area */}
                {strikeLevel != null && chartKitDatas.length > 0 ? (
                  <Line
                    x1={lineStartX}
                    y1={calcY(strikeLevel)}
                    x2={lineEndX}
                    y2={calcY(strikeLevel)}
                    stroke="black"
                    strokeWidth="2"
                    strokeDasharray="5,5"
                  />
                ) : null}

                {/* Barrier Level - Red dotted line (no text label) - constrained to chart area */}
                {barrierLevel != null && chartKitDatas.length > 0 ? (
                  <Line
                    x1={lineStartX}
                    y1={calcY(barrierLevel)}
                    x2={lineEndX}
                    y2={calcY(barrierLevel)}
                    stroke="red"
                    strokeWidth="2"
                    strokeDasharray="5,5"
                  />
                ) : null}

                {/* Tooltip vertical line and indicator circle - show exact point only when on a specific day */}
                {tooltip && tooltip.index >= 0 && tooltip.index < fullPeriodHistory.length ? (
                  <>
                    <Line
                      x1={tooltip.x}
                      y1={LINE_CHART_PADDING_TOP}
                      x2={tooltip.x}
                      y2={calcY(chartYMin)}
                      stroke="#666"
                      strokeWidth="1"
                      strokeDasharray="3,3"
                      opacity={0.5}
                    />
                    {tooltip.values.map((item, i) => (
                      <Circle
                        key={`${item.name}-${i}`}
                        cx={tooltip.x}
                        cy={calcY(item.value)}
                        r={i === 0 ? 6 : 5}
                        fill={item.color}
                        stroke="white"
                        strokeWidth={2}
                      />
                    ))}
                  </>
                ) : null}
              </>
            );
          }}
        />
            </View>
            {isPremiumProduct && underlyingPriceRange != null && (() => {
              const range: { min: number; max: number } = underlyingPriceRange;
              const internalPaddingTop = 16;
              const internalPaddingBottom = 64;
              const warrantRange = warrantDataMax - warrantDataMin || 1;
              const priceSpan = range.max - range.min;
              const ticks = [1, 0.75, 0.5, 0.25, 0];
              return (
                <View style={{ width: 48, height: chartHeight, justifyContent: 'space-between', paddingVertical: internalPaddingTop, paddingBottom: internalPaddingBottom }}>
                  {ticks.map((t) => {
                    const value = chartYMin + t * (chartYMax - chartYMin);
                    const price = range.min + ((value - warrantDataMin) / warrantRange) * priceSpan;
                    return (
                      <Text key={t} style={{ fontSize: 9, color: 'rgba(0,0,0,0.5)' }}>
                        {price.toFixed(1)}
                      </Text>
                    );
                  })}
                </View>
              );
            })()}
        </View>
        </ScrollView>
        </View>
      </View>
    );
  };

  const containerStyle = isDesktopCombined 
    ? { flex: 1, overflow: 'hidden' as const } 
    : [styles.section, Platform.OS === 'web' && { padding: 16, marginBottom: 12 }];

  return (
    <View 
      style={containerStyle}
      onLayout={(event) => {
        const { width } = event.nativeEvent.layout;
        if (width > 0 && width !== containerWidth) {
          setContainerWidth(width);
        }
      }}
    >
      <Text style={styles.sectionTitle}>Performance</Text>
      {!isDesktopCombined && <View style={styles.titleSeparator} />}

      {/* Tabs */}
      <View style={styles.tabsContainer}>
        <TouchableOpacity
          style={[styles.tab, activeTab === 'Week' && styles.tabActive]}
          onPress={() => onTabChange('Week')}
        >
          <Text style={[styles.tabText, activeTab === 'Week' && styles.tabTextActive]}>Week</Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.tab, activeTab === 'Month' && styles.tabActive]}
          onPress={() => onTabChange('Month')}
        >
          <Text style={[styles.tabText, activeTab === 'Month' && styles.tabTextActive]}>
            Month
          </Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.tab, activeTab === 'Year' && styles.tabActive]}
          onPress={() => onTabChange('Year')}
        >
          <Text style={[styles.tabText, activeTab === 'Year' && styles.tabTextActive]}>Year</Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.tab, activeTab === 'YTD' && styles.tabActive]}
          onPress={() => onTabChange('YTD')}
        >
          <Text style={[styles.tabText, activeTab === 'YTD' && styles.tabTextActive]}>
            YTD
          </Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.tab, activeTab === 'All' && styles.tabActive]}
          onPress={() => onTabChange('All')}
        >
          <Text style={[styles.tabText, activeTab === 'All' && styles.tabTextActive]}>All</Text>
        </TouchableOpacity>
      </View>

      {/* Chart with tooltip wrapper - tooltip outside overflow containers */}
      <View
        key={`chart-${activeTab}-${priceHistory.length}-${containerWidth}`}
        style={{ width: '100%', position: 'relative' }}
      >
        {/* Chart container with overflow hidden */}
        <View style={{ width: '100%', overflow: 'hidden' }}>
          {renderChart()}
        </View>

        {/* Tooltip overlay - positioned outside overflow container */}
        {tooltip && (
          <View 
            style={[
              {
                // Position relative to chart wrapper
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: Platform.OS === 'web' ? 99998 : 9999,
              }
            ]}
            pointerEvents="none"
          >
            <View 
              style={[styles.tooltipContainer, { 
                // Position tooltip to avoid mouse cursor and screen edges
                // Calculate position relative to chart container
                left: (() => {
                  const tooltipWidth = 200;
                  // Get chart container position - need to account for chart padding
                  const chartPaddingLeft = 64; // Y-axis labels width
                  const actualX = tooltip.x + chartPaddingLeft;
                  const rightSpace = actualChartWidth - actualX;
                  const leftSpace = actualX;
                  
                  if (rightSpace > tooltipWidth + 30) {
                    // Enough space on right, place to the right of indicator
                    return actualX + 20;
                  } else if (leftSpace > tooltipWidth + 30) {
                    // Not enough space on right, place to the left of indicator
                    return Math.max(10, actualX - tooltipWidth - 20);
                  } else {
                    // Not enough space on either side, center it
                    return Math.max(10, (actualChartWidth - tooltipWidth) / 2);
                  }
                })(),
                // Position tooltip above or below indicator, always visible
                top: (() => {
                  const tooltipHeight = 150;
                  const chartPaddingTop = 16; // Internal padding top
                  const actualY = tooltip.y + chartPaddingTop;
                  const spaceBelow = chartHeight - actualY;
                  const spaceAbove = actualY;
                  
                  // Prefer placing tooltip above the indicator to avoid covering data
                  if (spaceAbove > tooltipHeight + 20) {
                    // Enough space above, place above indicator
                    return Math.max(10, actualY - tooltipHeight - 10);
                  } else if (spaceBelow > tooltipHeight + 20) {
                    // Not enough space above, place below indicator (closer to indicator)
                    return Math.min(actualY + 10, chartHeight - tooltipHeight - 10);
                  } else {
                    // Not enough space on either side, place in middle
                    return Math.max(10, (chartHeight - tooltipHeight) / 2);
                  }
                })(),
                // Ensure tooltip is always on top with maximum z-index
                zIndex: Platform.OS === 'web' ? 99999 : 10000,
              }]}
              {...(Platform.OS === 'web' && {
                // On web, use native style to ensure z-index is applied
                // @ts-ignore - React Native Web supports native style prop
                nativeStyle: {
                  zIndex: 99999,
                },
              })}
            >
              <Text style={styles.tooltipDate}>{tooltip.date}</Text>
              {tooltip.values.map((item, i) => {
                const isPremiumHere = PREMIUM_PRODUCT_CODES.includes(String(product?.productCode ?? ''));
                const showPriceAsPrimary = isPremiumHere && i > 0 && item.price != null && item.price > 0;
                return (
                  <View key={i} style={styles.tooltipRow}>
                    <View style={[styles.tooltipDot, { backgroundColor: item.color }]} />
                    <View style={styles.tooltipValueContainer}>
                      <Text style={styles.tooltipLabel}>{item.name}:</Text>
                      <View style={styles.tooltipValuesRow}>
                        {showPriceAsPrimary ? (
                          <Text style={styles.tooltipValue}>{item.price!.toFixed(2)}</Text>
                        ) : (
                          <>
                            <Text style={styles.tooltipValue}>{item.value.toFixed(2)}%</Text>
                            {item.price !== undefined && item.price > 0 && (
                              <Text style={styles.tooltipPrice}>{' '}({item.price.toFixed(2)})</Text>
                            )}
                          </>
                        )}
                      </View>
                    </View>
                  </View>
                );
              })}
            </View>
          </View>
        )}
      </View>

      {/* Legend */}
      <View style={styles.legendContainer}>
        <View style={styles.legendItem}>
          <View style={[styles.legendColor, { backgroundColor: PRODUCT_LINE_COLOR }]} />
          <Text style={styles.legendText}>Product</Text>
        </View>
        {showUnderlyingsInChart && underlyingsHistory.map((underlying, index) => {
          const isSelected = selectedUnderlyingIndex === index;

          return (
            <TouchableOpacity
              key={index}
              onPress={() => {
                if (isFocused && isSelected) {
                  // “desclickear” -> volver a todas
                  onShowAllUnderlyings?.();
                } else {
                  // foco a este underlying
                  onSelectUnderlyingIndex?.(index);
                }
              }}
              style={[
                styles.legendItem,
                isFocused && !isSelected && { opacity: 0.5 },
              ]}
            >
              <View
                style={[
                  styles.legendColor,
                  { backgroundColor: UNDERLYING_COLORS[index % UNDERLYING_COLORS.length] },
                ]}
              />
              <Text style={styles.legendText}>
                {underlying.name}
                {isFocused && isSelected ? ' (focused)' : ''}
              </Text>
            </TouchableOpacity>
          );
        })}
        {product?.strikeLevel ? (
          <View style={styles.legendItem}>
            <View style={[styles.legendDashedLine, { borderColor: 'black' }]} />
            <Text style={styles.legendText}>
              Strike Level ({product.strikeLevel.toFixed(0)}%)
            </Text>
          </View>
        ) : null}
        {product?.barrierLevel ? (
          <View style={styles.legendItem}>
            <View style={[styles.legendDashedLine, { borderColor: 'red' }]} />
            <Text style={styles.legendText}>
              Barrier Level ({product.barrierLevel.toFixed(0)}%)
            </Text>
          </View>
        ) : null}
      </View>

      {/* Performance Value */}
      <View style={styles.performanceValueContainer}>
        <Text style={styles.performanceLabel}>Performance</Text>
        <Text
          style={[
            styles.performanceValue,
            Number(calculatePerformance()) < 0 && styles.performanceValueNegative,
          ]}
        >
          {calculatePerformance()}%
        </Text>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  section: {
    backgroundColor: '#FFFFFF',
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    padding: 16,
    marginBottom: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.08,
    shadowRadius: 8,
    elevation: 2,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#1F2937',
    marginBottom: 16,
  },
  titleSeparator: {
    height: 1,
    backgroundColor: '#E5E7EB',
    marginTop: -8,
    marginBottom: 16,
  },
  tabsContainer: {
    flexDirection: 'row',
    backgroundColor: '#F5F5F5',
    borderRadius: 4,
    padding: 2,
    marginBottom: 16,
  },
  tab: {
    flex: 1,
    paddingVertical: 8,
    alignItems: 'center',
    borderRadius: 4,
  },
  tabActive: {
    backgroundColor: '#FFFFFF',
  },
  tabText: {
    fontSize: 14,
    color: '#757575',
  },
  tabTextActive: {
    color: '#212121',
    fontWeight: '500',
  },
  chartLoadingContainer: {
    minHeight: 220,
    justifyContent: 'center',
    alignItems: 'center',
  },
  noDataText: {
    fontSize: 14,
    color: '#757575',
  },
  chartContainer: {
    minHeight: 240,
    width: '100%',
    marginVertical: 8,
    ...(Platform.OS === 'web' && {
      minHeight: 340,
    }),
  },
  chartScrollContent: {
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    marginLeft: -35,
  },
  legendContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 16,
    marginTop: 12,
    marginBottom: 16,
  },
  legendItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  legendColor: {
    width: 16,
    height: 3,
    borderRadius: 1.5,
  },
  legendDashedLine: {
    width: 16,
    height: 0,
    borderWidth: 1,
    borderStyle: 'dashed',
  },
  legendText: {
    fontSize: 12,
    color: '#212121',
  },
  performanceValueContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: '#E0E0E0',
  },
  performanceLabel: {
    fontSize: 14,
    color: '#757575',
  },
  performanceValue: {
    fontSize: 16,
    fontWeight: '600',
    color: '#4CAF50',
  },
  performanceValueNegative: {
    color: '#F44336',
  },
  tooltipOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 9999,
    ...(Platform.OS === 'web' && {
      zIndex: 9999,
      // Ensure tooltip is above everything on web
      pointerEvents: 'none',
    }),
  },
  tooltipContainer: {
    position: 'absolute',
    backgroundColor: 'white',
    borderRadius: 8,
    padding: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 6,
    elevation: 10,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    minWidth: 160,
    maxWidth: 220,
    zIndex: 10000,
    ...(Platform.OS === 'web' && {
      zIndex: 10000,
      // Ensure tooltip container is above everything on web
      // Use !important via inline style if needed
    }),
  },
  tooltipDate: {
    fontSize: 13,
    fontWeight: '600',
    color: '#1F2937',
    marginBottom: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
    paddingBottom: 6,
  },
  tooltipRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginVertical: 4,
  },
  tooltipDot: {
    width: 10,
    height: 10,
    borderRadius: 5,
    marginRight: 8,
    marginTop: 2,
  },
  tooltipValueContainer: {
    flex: 1,
  },
  tooltipLabel: {
    fontSize: 12,
    color: '#6B7280',
    marginBottom: 2,
    fontWeight: '500',
  },
  tooltipValuesRow: {
    flexDirection: 'row',
    alignItems: 'baseline',
    flexWrap: 'wrap',
  },
  tooltipValue: {
    fontSize: 13,
    fontWeight: '600',
    color: '#1F2937',
  },
  tooltipPrice: {
    fontSize: 11,
    color: '#6B7280',
    fontWeight: '400',
  },
});
